import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    BedDouble,
    BedIcon,
    ChevronLeft,
    ChevronRight,
    Scale3DIcon,
    LocateIcon,
    Building2,
    ListCheck,
    Building,
    CalendarCheck,
    CalendarCheck2,
    Layers,
    Ruler,
    Briefcase,
    Calendar,
    MapPin,
    DollarSign,
    BadgeCheck,
    Compass,
    Car,
    Sparkles,
    ShieldCheck,
    Droplet,
    PhoneCallIcon
} from "lucide-react";
import { useSwipeable } from 'react-swipeable';
import FloorPlanPrice from './FloorPlanPrice';
import EnquiryForm from '../Forms/EnquiryForm';
import EnquiryFormModal from '../Forms/EnquiryFormModal';

const formatPrice = (value) => {
    const num = Number(value);
    if (isNaN(num) || num <= 0) return 'N/A';

    if (num >= 1e7) {
        return `${(num / 1e7).toFixed(2)} Crore`;
    } else if (num >= 1e5) {
        return `${(num / 1e5).toFixed(2)} Lakh`;
    } else {
        return num.toLocaleString('en-IN'); // fallback for values < 1 Lakh
    }
};


const getPriceAndAreaDetails = (flatTypeArray) => {
    let smallest = null;
    let largest = null;

    flatTypeArray.forEach(ft => {
        if (Array.isArray(ft.builtUpAreas)) {
            ft.builtUpAreas.forEach(area => {
                const price = parseFloat(area.expectedPrice || 0);
                if (!isNaN(price) && price > 0) {
                    if (!smallest || price < parseFloat(smallest.expectedPrice)) smallest = area;
                    if (!largest || price > parseFloat(largest.expectedPrice)) largest = area;
                }
            });
        }
    });

    return { smallest, largest };
};

const RentDetailPage = () => {
    const { id } = useParams();
    const [data, setData] = useState({});
    const [imageData, setImageData] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [smallest, setSmallest] = useState(null);
    const [largest, setLargest] = useState(null);
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const [open, setOpen] = useState(false);

    const fetchDataSingle = async () => {
        try {
            const response = await fetch(`https://100real.xyz/rentresident/getsingleproperty/${id}`);
            const res = await response.json();

            const parseJSON = (input) => {
                if (typeof input === 'string') {
                    try {
                        return JSON.parse(input);
                    } catch {
                        return [];
                    }
                }
                return Array.isArray(input) ? input : [];
            };

            res.amenities = parseJSON(res.amenities);
            res.carpetArea = parseJSON(res.carpetArea);
            res.flatType = parseJSON(res.flatType);
            res.formImages = parseJSON(res.formImages);
            res.locationAdvantage = parseJSON(res.locationAdvantage);
            res.locationAdvantages = parseJSON(res.locationAdvantages);
            res.security = parseJSON(res.security);
            res.water_supply = parseJSON(res.water_supply);

            setData(res);
            console.log(res)
            setImageData(res.formImages || []);

            const { smallest, largest } = getPriceAndAreaDetails(res.flatType || []);
            setSmallest(smallest);
            setLargest(largest);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchDataSingle();
    }, []);

    // Autoplay slider
    useEffect(() => {
        if (imageData.length > 1 && !isPaused) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % imageData.length);
            }, 3000);
        }
        return () => clearInterval(intervalRef.current);
    }, [imageData, isPaused]);

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % imageData.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + imageData.length) % imageData.length);
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => handleNext(),
        onSwipedRight: () => handlePrev(),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
    });

    const formatCarpetAreas = (flatTypes = []) => {
        const areas = [];

        flatTypes.forEach(ft => {
            if (Array.isArray(ft.builtUpAreas)) {
                ft.builtUpAreas.forEach(areaObj => {
                    const area = parseFloat(areaObj?.value);
                    if (!isNaN(area)) {
                        areas.push(area);
                    }
                });
            }
        });

        if (areas.length === 0) return '';

        const sorted = areas.sort((a, b) => a - b);
        const lowest = sorted[0];
        const largest = sorted[sorted.length - 1];

        return lowest === largest ? `${lowest} sqft` : `${lowest} sqft - ${largest} sqft`;
    };

    function getOrdinal(n) {
        const s = ["th", "st", "nd", "rd"],
            v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }

    const formatBedrooms = (flatTypes = []) => {
        const bedroomDetails = [];
        const jodiDetails = [];

        const extractNumber = (str = '') => {
            const match = str.match(/^(\d+)/);
            return match ? parseInt(match[1], 10) : null;
        };

        // Filter out only those flats which have valid price
        const validFlats = flatTypes.filter(flat =>
            Array.isArray(flat.builtUpAreas) &&
            flat.builtUpAreas.some(area => area?.expectedPrice && area.expectedPrice > 0)
        );

        if (validFlats.length === 0) return ''; // If none have valid price, return blank

        validFlats.forEach(flat => {
            const typeStr = (flat.type || '').toUpperCase();

            const match = typeStr.match(/^(\d+)/);
            if (match) {
                const count = parseInt(match[1], 10);
                if (!isNaN(count)) {
                    const suffix = typeStr.includes('RK') ? 'RK' : 'BHK';
                    bedroomDetails.push({ count, suffix });
                }
            }

            const jodi1 = extractNumber(flat.jodi1);
            const jodi2 = extractNumber(flat.jodi2);
            if (jodi1 && jodi2) {
                jodiDetails.push(`Jodi (${jodi1}+${jodi2})`);
            }
        });

        if (bedroomDetails.length === 0) return jodiDetails.join(', ');

        bedroomDetails.sort((a, b) => a.count - b.count);
        const lowest = bedroomDetails[0];
        const highest = bedroomDetails[bedroomDetails.length - 1];

        let bedroomString = lowest.count === highest.count && lowest.suffix === highest.suffix
            ? `${lowest.count} ${lowest.suffix}`
            : `${lowest.count} ${lowest.suffix} - ${highest.count} ${highest.suffix}`;

        if (jodiDetails.length > 0) {
            const uniqueJodis = [...new Set(jodiDetails)];
            bedroomString += `, ${uniqueJodis.join(', ')}`;
        }

        return bedroomString;
    };



    return (
        <>
            <div className="detail-page-sp0003">
                <div className="detail-page-header-sp">
                    <h1>
                        {data.building_name
                            ? data.building_name.charAt(0).toUpperCase() + data.building_name.slice(1)
                            : ''}
                    </h1>
                </div>

                <div className="detail-page-main-section-sp">
                    <div
                        className="detail-page-images-section-sp"
                        {...swipeHandlers}
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                        onTouchStart={() => setIsPaused(true)}
                        onTouchEnd={() => setIsPaused(false)}
                    >
                        {imageData.length > 0 && (
                            <>
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={imageData[currentIndex]}
                                        src={`https://100real.xyz/Images/Store/${imageData[currentIndex]}`}
                                        alt={`Slide ${currentIndex + 1}`}
                                        onError={(e) => (e.target.style.display = 'none')}
                                        initial={{ opacity: 0, x: 100 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -100 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </AnimatePresence>

                                {imageData.length > 1 && (
                                    <>
                                        <button onClick={handlePrev} className="detail-image-slider-button-sp left">
                                            <ChevronLeft size={24} />
                                        </button>
                                        <button onClick={handleNext} className="detail-image-slider-button-sp right">
                                            <ChevronRight size={24} />
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    <div className="detail-page-info-section-sp">
                        <button className="brokeage-btn-detail-page-sp">
                            <Building />
                            {data.typeofproperty === 'Rent Residential' ? 'Rent' : data.typeofproperty}
                        </button>

                        {data.availability && (
                            <button className="brokeage-btn-detail-page-sp detail-page-left-margin">
                                <CalendarCheck2 />
                                {data.availability}
                            </button>
                        )}

                        {(smallest?.expectedPrice > 0 || largest?.expectedPrice > 0) && (
                            <h2 className="detail-page-info-price-sp">
                                {smallest?.expectedPrice === largest?.expectedPrice
                                    ? `₹ ${formatPrice(smallest.expectedPrice)}`
                                    : `₹ ${formatPrice(smallest.expectedPrice)} - ₹ ${formatPrice(largest.expectedPrice)}`
                                }
                            </h2>
                        )}

                        <div className="detail-page-info-speciality-sp">
                            <p>
                                <BedDouble /> {formatBedrooms(data.flatType)}
                            </p>
                            <p>
                                <Scale3DIcon /> {formatCarpetAreas(data.flatType)}
                            </p>
                        </div>
                        <div className="detail-building-address-sp">
                            <MapPin size={20} /><p> {data.address}, {data.city}</p>
                        </div>
                        {(data.floorLevel || data.totalFloors) && (
                            <div className="detail-page-floor-total-floor ">
                                <Building2 />
                                <p>
                                    {data.floorLevel && getOrdinal(data.floorLevel)}
                                    {data.floorLevel && data.totalFloors && " of "}
                                    {data.totalFloors && getOrdinal(data.totalFloors) + " Floors"}
                                </p>
                            </div>
                        )}

                        {data.booking_amount > 0 ? (
                            <p className="detail-page-booking-amount-sp">
                                Booking Amount: ₹ {formatPrice(data.booking_amount)}
                            </p>
                        ) : (
                            <button
                                className="detail-page-booking-amount-sp detail-page-booking-ask-btn"
                            >
                                Booking Amount: <span onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>Ask for details</span>
                            </button>
                        )}

                        <div className="detail-page-contact-btn-sp" >
                            <button onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>Talk to an expert</button>
                            {
                                data && data.virtual_tour === "" ? null :
                                    <button onClick={() => window.open(data.virtual_tour, '_blank')}>Virtual tour</button>
                            }
                        </div>
                    </div>
                </div>

                <div className="detail-page-main-sections-sp">
                    <div className="detail-page-main-left-sections-sp">
                        {data?.property_description && (
                            <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                <div className="detail-page-main-section-heading-sp">
                                    <h2>
                                        About{" "}
                                        {data.building_name
                                            ? data.building_name.charAt(0).toUpperCase() + data.building_name.slice(1)
                                            : ""}
                                    </h2>
                                </div>
                                <div className="detail-page-main-section-content-sp">
                                    <p>{data.property_description}</p>
                                </div>
                            </div>
                        )}

                        {(
                            data?.availability ||
                            formatBedrooms(data?.flatType) ||
                            formatCarpetAreas(data?.flatType) ||
                            data?.developer_name ||
                            data?.ageOfBuilding ||
                            data?.city ||
                            data?.maintenance ||
                            data?.ownership ||
                            data?.facing ||
                            data?.parking
                        ) && (
                                <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                    <div className="detail-page-main-section-heading-sp">
                                        <h2>
                                            Overview of{" "}
                                            {data.building_name?.charAt(0).toUpperCase() + data.building_name?.slice(1)}
                                        </h2>
                                    </div>

                                    <div className="detail-page-main-section-content-sp">
                                        <div className="custom-overview-grid">
                                            {data.availability && (
                                                <div className="custom-overview-item">
                                                    <Building2 className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Project Status</p>
                                                        <p className="custom-overview-value">{data.availability}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {formatBedrooms(data.flatType) && (
                                                <div className="custom-overview-item">
                                                    <Layers className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Configuration</p>
                                                        <p className="custom-overview-value">{formatBedrooms(data.flatType)}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {formatCarpetAreas(data.flatType) && (
                                                <div className="custom-overview-item">
                                                    <Ruler className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Unit Size</p>
                                                        <p className="custom-overview-value">{formatCarpetAreas(data.flatType)}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.developer_name && (
                                                <div className="custom-overview-item">
                                                    <Briefcase className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Builder</p>
                                                        <p className="custom-overview-value">{data.developer_name}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.ageOfBuilding && (
                                                <div className="custom-overview-item">
                                                    <Calendar className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Age of Build</p>
                                                        <p className="custom-overview-value">{data.ageOfBuilding}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.city && (
                                                <div className="custom-overview-item">
                                                    <MapPin className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">City</p>
                                                        <p className="custom-overview-value">{data.city}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.maintenance && (
                                                <div className="custom-overview-item">
                                                    <DollarSign className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Monthly Maintenance</p>
                                                        <p className="custom-overview-value">{data.maintenance}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.ownership && (
                                                <div className="custom-overview-item">
                                                    <BadgeCheck className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Ownership</p>
                                                        <p className="custom-overview-value">{data.ownership}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.facing && (
                                                <div className="custom-overview-item">
                                                    <Compass className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Facing Direction</p>
                                                        <p className="custom-overview-value">{data.facing}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {data.parking && (
                                                <div className="custom-overview-item">
                                                    <Car className="custom-overview-icon" />
                                                    <div className="custom-overview-text">
                                                        <p className="custom-overview-label">Parking</p>
                                                        <p className="custom-overview-value">{data.parking}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                        {data.amenities?.length > 0 && (
                            <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                <div className="detail-page-main-section-heading-sp">
                                    <h2>Modern Comforts & Amenities</h2>
                                </div>
                                <div className="detail-page-main-section-content-sp detail-page-main-section-amenities-sp">
                                    {data.amenities.map((ele, idx) => (
                                        <div className="amenity-item-sp" key={idx}>
                                            <Sparkles size={18} className="amenity-icon-sp" />
                                            <span className="amenity-label-sp">{ele}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <FloorPlanPrice data={data} />

                        {data.locationAdvantage?.length > 0 && (
                            <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                <div className="detail-page-main-section-heading-sp">
                                    <h2>Prime Location Benefits</h2>
                                </div>
                                <div className="detail-page-main-section-content-sp detail-page-main-section-amenities-sp">
                                    {data.locationAdvantage.map((ele, idx) => (
                                        <div className="amenity-item-sp" key={idx}>
                                            <MapPin size={18} className="amenity-icon-sp" />
                                            <span className="amenity-label-sp">{ele}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {data.water_supply?.length > 0 && (
                            <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                <div className="detail-page-main-section-heading-sp">
                                    <h2>Reliable Water Supply</h2>
                                </div>
                                <div className="detail-page-main-section-content-sp detail-page-main-section-amenities-sp">
                                    {data.water_supply.map((ele, idx) => (
                                        <div className="amenity-item-sp" key={idx}>
                                            <Droplet size={18} className="amenity-icon-sp" />
                                            <span className="amenity-label-sp">{ele}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {data.security?.length > 0 && (
                            <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                <div className="detail-page-main-section-heading-sp">
                                    <h2>Safety & Security Features</h2>
                                </div>
                                <div className="detail-page-main-section-content-sp detail-page-main-section-amenities-sp">
                                    {data.security.map((ele, idx) => (
                                        <div className="amenity-item-sp" key={idx}>
                                            <ShieldCheck size={18} className="amenity-icon-sp" />
                                            <span className="amenity-label-sp">{ele}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {data?.dev_logo || data?.developer_name || data?.developer_description ? (
                            <div className="detail-page-main-section-container-sp detail-page-about-property-sp">
                                <div className="detail-page-main-section-heading-sp">
                                    <h2>Developer Info</h2>
                                </div>
                                <div className="detail-page-main-section-content-sp">
                                    <div className="developer-info-container-sp">
                                        {data.dev_logo && (
                                            <div className="developer-logo-sp">
                                                <img
                                                    src={`https://100real.xyz/Images/Store/${data.dev_logo}`}
                                                    alt="Developer Logo"
                                                />
                                            </div>
                                        )}
                                        <div className="developer-text-sp">
                                            <h3 className="developer-title-sp">{data.developer_name}</h3>
                                            <p className="developer-description-sp">
                                                {data.developer_description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                    </div>
                    <div className="detail-page-main-right-sections-sp">
                        <EnquiryForm source="Property Detail Page" user_id="HBTR0003" />
                    </div>
                </div>
            </div>


            <EnquiryFormModal isOpen={open} onClose={() => setOpen(false)} source="Property Detail Page" user_id="HBTR0003" />
        </>
    );
};

export default RentDetailPage;
